import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";

import { PlayerBanner } from "@/components/banner/player-banner";
import { PlayerClubs } from "@/components/player/player-clubs";
import { PlayerInformations } from "@/components/player/player-informations";
import { PlayerMatch } from "@/components/player/player-match";
import { PlayerRanking } from "@/components/player/player-ranking";
import { PlayerSpecifications } from "@/components/player/player-specifications";
import { PlayerTopVictory } from "@/components/player/player-top-victory";
import { PlayerTournament } from "@/components/player/player-tournament";
import { PremiumBanner } from "@/components/premium-upsell";
import { ProfileSkeleton } from "@/components/skeleton/profile-skeleton";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useMe } from "@/context/user-context";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/use-user-role";
import { useTRPC } from "@/lib/trpc";
import { routes } from "@/routes";

export function PlayerProfile() {
  const { user } = useMe();
  const { id } = useParams();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data, status } = useQuery(trpc.player.getPlayer.queryOptions({ playerId: id || user.id }));
  const navigate = useNavigate();
  const breakpoints = useBreakpoints();
  const { t } = useTranslation(["common", "profile"]);
  const userRole = useUserRole();
  const location = useLocation();
  const fromPath = location.state?.fromPath;
  const { toast } = useToast();

  const publicStatusMutation = useMutation(trpc.player.updatePlayerPublicStatus.mutationOptions({
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      toast({
        title: t("common:change-saved"),
        description:
        variables.isPublic
          ? "Le joueur est maintenant visible dans la liste."
          : "Le joueur n'est plus visible dans la liste.",
      });
    },
    onError: () => {
      toast({
        title: t("common:error-occured"),
        description: "Une erreur est survenue, veuillez réessayer",
      });
    },
  }));

  const showcaseStatusMutation = useMutation(trpc.player.updatePlayerShowcaseStatus.mutationOptions({
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      toast({
        title: t("common:change-saved"),
        description: variables.isShowcase
          ? "Le joueur est maintenant affiché en vedette."
          : "Le joueur n'est plus affiché en vedette.",
      });
    },
    onError: () => {
      toast({
        title: t("common:error-occured"),
        description: "Une erreur est survenue, veuillez réessayer",
      });
    },
  }));

  if (status === "pending") {
    return <ProfileSkeleton />;
  }
  if (status !== "success") {
    return null;
  }

  return (
    <>
      <div className="container flex flex-col gap-4 px-2 py-4 md:p-4">
        { fromPath && (
          <Button variant="outline" className="w-fit" onClick={() => navigate(-1)}>
            <CircleArrowLeft className="text-orange-500" />
            {fromPath === routes.favorites ? t("common:favorites-list") : t("common:players-list")}
          </Button>
        )}

        { userRole === "player" && fromPath !== routes.admin && data.isNew && <PlayerBanner />}

        {/* Fiche consultée par une structure freemium : l'identité a été
            remplacée côté serveur, on dit pourquoi et ce que l'abonnement
            débloque. Un profil vitrine s'affiche en clair et n'a pas de
            bandeau. */}
        {data.isAnonymized && (
          <PremiumBanner description={t("common:freemium.player-profile-banner")} />
        )}

        {user.isAdmin && (
          <div className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <label htmlFor="player-public-status" className="font-medium text-neutral-500">
                Le joueur est visible dans la liste
              </label>

              <Switch
                id="player-public-status"
                checked={publicStatusMutation.isPending ? publicStatusMutation.variables.isPublic : data.isPublic}
                disabled={publicStatusMutation.isPending}
                onCheckedChange={(isPublic) => {
                  publicStatusMutation.mutate({
                    playerIdToUpdate: data.userId,
                    isPublic,
                  });
                }}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <label htmlFor="player-showcase-status" className="font-medium text-neutral-500">
                Le joueur est affiché en vedette
              </label>

              <Switch
                id="player-showcase-status"
                checked={showcaseStatusMutation.isPending ? showcaseStatusMutation.variables.isShowcase : Boolean(data.isShowcase)}
                disabled={showcaseStatusMutation.isPending}
                onCheckedChange={(isShowcase) => {
                  showcaseStatusMutation.mutate({
                    playerIdToUpdate: data.userId,
                    isShowcase,
                  });
                }}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 rounded-[20px] bg-white p-2 shadow-sm md:grid-cols-2">
          <PlayerInformations player={data} />

          <div className="flex flex-col gap-3">
            <h3 className="px-2 pt-1 font-semibold leading-[1.1] text-neutral-500">{t("profile:availabilities")}</h3>
            <PlayerMatch player={data} />
            <PlayerTournament player={data} />
          </div>
        </div>

        {["xs", "sm"].includes(breakpoints) && (
          <div className="flex flex-col gap-4">
            <PlayerRanking player={data} />
            <PlayerSpecifications player={data} />
            <PlayerTopVictory player={data} />
            <PlayerClubs clubs={data.playerClubs} userId={data.userId} />
          </div>
        )}

        {["md", "lg", "xl"].includes(breakpoints) && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-4">
              <PlayerRanking player={data} />
              <PlayerTopVictory player={data} />
            </div>
            <div className="flex flex-col gap-4">
              <PlayerSpecifications player={data} />
              <PlayerClubs clubs={data.playerClubs} userId={data.userId} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
